import { ref, type Ref } from "vue";
import { BaseService, ErrorResponse } from "../base";
import type { Klimabilanzeintrag, Massnahme, Teilnahme } from "../datenjahr/model";

export const BerichtServiceSymbol = Symbol();
export const LocalStorageSelectedDatenjahr = "oekoprofit_selectedDatenjahr";
export const LocalStorageSelectedTeilnahme = "oekoprofit_selectedTeilnahme";

export interface IBerichtItemRepository {
  Get(berichtId: string): Promise<Blob | ErrorResponse>;
  GetByDatenjahr(berichtId: string, datenjahr: number, berichtszweck: string, erstellerin: string): Promise<Blob | ErrorResponse>;
  GetDaten(datenjahr: number): Promise<StandortDatensatz[] | ErrorResponse>;
  GetMassnahmen(): Promise<Massnahme[] | ErrorResponse>;
  GetMassnahmenBroschuere(): Promise<Massnahme[] | ErrorResponse>;
  GetKlimabilanzen(datenjahr: number): Promise<Klimabilanzeintrag[] | ErrorResponse>;
  GetDekarbonisierungsstrategien(): Promise<DekarbonisierungsstrategieDatensatz[] | ErrorResponse>;
  GetEnergietraeger(): Promise<EnergietraegerDatensatz[] | ErrorResponse>;
}

export interface DekarbonisierungsstrategieDatensatz {
  standortId: string;
  name: string;
  zielInProzent?: number;
  bisJahr?: number;
  basisjahr?: number;
  jaehrlichesReduktionsziel?: number;
  reduktionsziel2030?: number;
  reduktionsziel2040?: number;
  beschreibung?: string;
  abweichungen?: string;
}

export interface EnergietraegerDatensatz {
  datenjahr: number;
  teilnehmer: string;
  energietraeger: string;
  energiemengeKWh: number;
  menge: number;
  einheit: string;
}

export interface StandortDatensatz{
  standort: string,
  gruenflaechen: number,
  anteilElektroAnFuhrpark: number,
  emissionenInTonnenCo2: number,
}

export class BerichtRepository implements IBerichtItemRepository {
  private _service: BaseService;
  private _datenjahr: number;

  constructor(service: BaseService, datenjahr: number) {
    this._service = service;
    this._datenjahr = datenjahr;
  }

  async Get(berichtId: string): Promise<Blob | ErrorResponse> {
    let result: Blob | ErrorResponse = await this._service.Get<Blob>(`${berichtId}/${this._datenjahr}`, true, true);

    return result;
  }

  async GetDaten(datenjahr: number): Promise<StandortDatensatz[] | ErrorResponse> {
    let result: StandortDatensatz[] | ErrorResponse = await this._service.Get<StandortDatensatz[]>(`daten/${datenjahr}`, true);

    return result;
  }

  async GetMassnahmen(): Promise<Massnahme[] | ErrorResponse> {
    let result: Massnahme[] | ErrorResponse = await this._service.Get<Massnahme[]>(`daten/massnahmen/alle`, true);

    return result;
  }
  async GetMassnahmenBroschuere(): Promise<Massnahme[] | ErrorResponse> {
    let result: Massnahme[] | ErrorResponse = await this._service.Get<Massnahme[]>(`daten/massnahmen/broschuere`, true);

    return result;
  }
  async GetKlimabilanzen(datenjahr: number): Promise<Massnahme[] | ErrorResponse> {
    let result: Massnahme[] | ErrorResponse = await this._service.Get<Massnahme[]>(`daten/klimabilanz/${datenjahr}`, true);

    return result;
  }

  async GetDekarbonisierungsstrategien(): Promise<DekarbonisierungsstrategieDatensatz[] | ErrorResponse> {
    let result: DekarbonisierungsstrategieDatensatz[] | ErrorResponse = await this._service.Get<DekarbonisierungsstrategieDatensatz[]>(`daten/dekarbonisierungsstrategien`, true);

    return result;
  }

  async GetEnergietraeger(): Promise<EnergietraegerDatensatz[] | ErrorResponse> {
    let result: EnergietraegerDatensatz[] | ErrorResponse = await this._service.Get<EnergietraegerDatensatz[]>(`daten/energietraeger`, true);

    return result;
  }

  async GetByDatenjahr(berichtId: string, datenjahr: number, berichtszweck: string, erstellerin: string): Promise<Blob | ErrorResponse> {
    let result: Blob | ErrorResponse = await this._service.Post<Blob>(`${berichtId}/${datenjahr}`, true, {
      zweck: berichtszweck,
      erstellerin: erstellerin
    }, true);
    return result;
  }
}

export interface IBerichtService {
  BerichtItem: IBerichtItemRepository;
}

export class BerichtService extends BaseService implements IBerichtService {
  private _datenjahr: Ref<number>;
  private _teilnahme: Ref<Teilnahme>;

  constructor(baseUri: string, token: string) {
    super(baseUri, token);

    this._datenjahr = ref<number>(null);
    if (localStorage.getItem(LocalStorageSelectedDatenjahr) != null) {
      this._datenjahr.value = JSON.parse(localStorage.getItem(LocalStorageSelectedDatenjahr));
    }

    this._teilnahme = ref<Teilnahme>(null);
    if (localStorage.getItem(LocalStorageSelectedTeilnahme)!= null) {
      this._teilnahme.value = JSON.parse(localStorage.getItem(LocalStorageSelectedTeilnahme));
    }
  }

  get BerichtItem(): IBerichtItemRepository {
    return new BerichtRepository(this, this._datenjahr.value);
  }
}